import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Case, CaseState, Outcome } from "../components/register/caseFlow";
import { CaseBody, CaseDetailFailure, moveCase, recordResolution } from "../components/register/CaseView";
import { Loading } from "../components/States";
import { apiGet } from "../lib/http";
import { useMe } from "../lib/me";

/** /cases/:id. One case: the finding it opened on, its resolutions, and its state moves. */
export function CaseDetail() {
  const { id = "" } = useParams();
  const { me } = useMe();
  const client = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["case", id],
    queryFn: () => apiGet("/api/v1/cases/{case_id}", { path: { case_id: id } }),
  });

  const settle = {
    onMutate: () => setProblem(null),
    onSuccess: (c: Case) => {
      client.setQueryData(["case", id], c);
      void client.invalidateQueries({ queryKey: ["cases"] });
      void client.invalidateQueries({ queryKey: ["asset", c.package, c.tag] });
    },
    // A 409 is the server refusing a move it does not allow, or a case changed by someone else.
    // Either way the reason is shown as given and the case is read again.
    onError: (e: unknown) => {
      setProblem(e instanceof Error ? e.message : String(e));
      void query.refetch();
    },
  };
  const resolve = useMutation({ mutationFn: (v: { outcome: Outcome; note: string }) => recordResolution(id, v.outcome, v.note), ...settle });
  const move = useMutation({ mutationFn: (to: CaseState) => moveCase(id, to), ...settle });

  return (
    <div className="page">
      <Link to="/cases" className="row muted" style={{ fontSize: 13, marginBottom: 8 }}>
        <ArrowLeft size={14} aria-hidden="true" /> All cases
      </Link>
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>
        Case <span className="mono">{id.slice(0, 8).toUpperCase() || id}</span>
      </h2>
      <p className="muted mono" style={{ fontSize: 12, marginBottom: 16 }}>{id}</p>

      {query.isPending && <Loading what="Loading the case" />}
      {query.isError && <CaseDetailFailure error={query.error} onRetry={() => void query.refetch()} />}
      {query.data && (
        <CaseBody c={query.data} me={me} busy={resolve.isPending || move.isPending} problem={problem}
                  onResolve={(outcome, note) => resolve.mutate({ outcome, note })} onMove={(to) => move.mutate(to)} />
      )}
    </div>
  );
}
